import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { DriverUserCredential } from '../../../models/driver-user-credential.model';
import { DriverUserCredentialService } from '../../../services/driver-user-credential.service';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-user-credential-modal',
  standalone: true,
  imports: [TranslocoDirective, TranslocoPipe],
  templateUrl: './driver-user-credential-modal.component.html',
  styleUrl: './driver-user-credential-modal.component.scss'
})
export class DriverUserCredentialModalComponent implements OnInit, OnDestroy {
  @Input() driverId!: number;
  @Input() hasLoginAccount = false;

  public credential: DriverUserCredential | null = null;
  public isLoading = false;
  public isResetting = false;
  public passwordRevealed = false;

  private accountGenerated = false;
  private readonly cancelSubject = new Subject<void>();

  private readonly activeModal = inject(NgbActiveModal);
  private readonly credentialService = inject(DriverUserCredentialService);
  private readonly confirmationModalService = inject(ConfirmationModalService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly toastr = inject(ToastrService);
  private readonly transloco = inject(TranslocoService);

  ngOnInit(): void {
    this.loadCredentials();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadCredentials(): Promise<void> {
    this.isLoading = true;
    try {
      if (this.hasLoginAccount) {
        await this.getCredentials();
      } else {
        await this.generateCredentials();
      }
      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
          this.activeModal.dismiss();
        }
      });
    }
  }

  private async getCredentials(): Promise<void> {
    const response = await this.credentialService.getUserCredentialsAsync(this.driverId, this.cancelSubject);
    this.credential = response ?? null;
    this.passwordRevealed = false;
  }

  private async generateCredentials(): Promise<void> {
    const response = await this.credentialService.generateUserCredentialsAsync(this.driverId, this.cancelSubject);
    this.credential = response ?? null;
    this.passwordRevealed = true;
    this.accountGenerated = true;
  }

  public async resetPassword(): Promise<void> {
    const confirmed = await this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('driverInformation.credentials.resetPrompt.title'),
      message: this.transloco.translate('driverInformation.credentials.resetPrompt.message'),
      warningMessage: this.transloco.translate('driverInformation.credentials.resetPrompt.warning'),
      confirmText: this.transloco.translate('driverInformation.credentials.resetPrompt.confirm'),
      declineText: this.transloco.translate('common.cancel')
    }).then(() => true, () => false);

    if (!confirmed) {
      return;
    }

    this.isResetting = true;
    try {
      const response = await this.credentialService.resetUserPasswordAsync(this.driverId, this.cancelSubject);
      if (response) {
        this.credential = response;
        this.passwordRevealed = true;
        this.toastr.success(this.transloco.translate('driverInformation.credentials.resetSuccess'));
      }
      this.isResetting = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => this.isResetting = false
      });
    }
  }

  public async copyPassword(): Promise<void> {
    const password = this.credential?.password;
    if (!password) {
      return;
    }

    try {
      await navigator.clipboard.writeText(password);
      this.toastr.success(this.transloco.translate('driverInformation.credentials.copied'));
    } catch {
      this.toastr.error(this.transloco.translate('driverInformation.credentials.copyFailed'));
    }
  }

  public downloadCredentials(): void {
    if (!this.credential) {
      return;
    }

    const { username, password } = this.credential;
    const usernameLabel = this.transloco.translate('common.username');
    const passwordLabel = this.transloco.translate('common.password');
    const contents = `${usernameLabel}: ${username}\n${passwordLabel}: ${password}\n`;
    const blob = new Blob([contents], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `driver-credentials-${username}.txt`;
    anchor.click();

    URL.revokeObjectURL(url);
  }

  public close(): void {
    this.activeModal.close(this.accountGenerated ? 'generated' : undefined);
  }
}
